import React, { useLayoutEffect, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';
import CheckIcon from 'icons/check.svg';
import ChevronDownIcon from 'icons/chevron-down.svg';
import ChevronUpIcon from 'icons/chevron-up.svg';
import HomeIcon from 'icons/home-sm.svg';
import CloseIcon from 'icons/ic-close.svg';
import InfoIcon from 'icons/info.svg';
import MinusIcon from 'icons/minus.svg';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { InView } from 'react-intersection-observer';
import Select, { components } from 'react-select';
import tailwindConfig from 'stylesheets/tailwind.config';

import { useInfiniteDivisionSearchesQuery } from 'components/hooks/divisionSearchQueries';
import useDebounce from 'components/hooks/useDebounce';

import { useProjectsAnalyticsDashboardContext } from 'components/contexts/ProjectsAnalyticsDashboardContext';
import {
  collectDomainCompanies,
  sortCompaniesByPrimaryAndName,
} from 'components/projectsAnalytics/helpers/filters';

const { spacing } = tailwindConfig.theme;

const MAX_PILLS_CONTAINER_HEIGHT = 130;
const MIN_VISIBLE_PILLS_COUNT = 3;

export default function ProjectsAnalyticsDivisionsFilter({
  divisionIdsFilter,
  onChange,
}) {
  const { divisionsTotalCount } = useProjectsAnalyticsDashboardContext();

  const [isSelectAllChecked, setIsSelectAllChecked] = useState(false);
  const [isSelectMenuOpen, setIsSelectMenuOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const [debouncedSearchValue] = useDebounce(searchValue, 250);

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'projectsAnalytics.components.ProjectsAnalyticsDivisionsFilter',
  });

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  const {
    data: domainDivisions,
    fetchNextPage: fetchNextDomainDivisionsPage,
    hasNextPage: hasNextDomainDivisionsPage,
    isFetching: isDomainDivisionsFetching,
  } = useInfiniteDivisionSearchesQuery({
    params: { filter: { admin_only: true }, search: debouncedSearchValue },
    queryOptions: {
      enabled: isSelectMenuOpen && divisionsTotalCount > 1,
      keepPreviousData: true,
    },
  });

  const handleLoadMore = (isInView) => {
    if (isInView && hasNextDomainDivisionsPage && !isDomainDivisionsFetching) {
      fetchNextDomainDivisionsPage();
    }
  };

  const handleRemoveFilterClick = () => {
    onChange([]);
    setIsSelectAllChecked(false);
    setSearchValue('');
  };

  const handleRemovePillClick = (divisionId) => {
    if (isSelectAllChecked) {
      onChange(
        domainDivisions.data
          .filter((division) => division.id !== divisionId)
          .map((division) => division.id),
      );

      setIsSelectAllChecked(false);
    } else {
      onChange(
        [...divisionIdsFilter].filter((filterId) => filterId !== divisionId),
      );
    }
  };

  const handleSelectChange = (options) => {
    if (!hasNextDomainDivisionsPage && options.length === divisionsTotalCount) {
      setIsSelectAllChecked(true);
      onChange([]);
    } else {
      setIsSelectAllChecked(false);

      const values = options.map((option) => option.value);

      onChange(
        isSelectAllChecked ?
          domainDivisions.data
            .filter((division) => !values.includes(division.id))
            .map((division) => division.id)
        : values,
      );
    }
  };

  const handleSelectAllToggle = async () => {
    if (isSelectAllChecked || divisionIdsFilter.size > 0) {
      setIsSelectAllChecked(false);
    } else {
      setIsSelectAllChecked(true);
    }

    onChange([]);
  };

  if (
    isSelectAllChecked &&
    hasNextDomainDivisionsPage &&
    !isDomainDivisionsFetching
  ) {
    fetchNextDomainDivisionsPage();
  }

  const totalPillsCount =
    isSelectAllChecked ? divisionsTotalCount : divisionIdsFilter.size;

  const isRemoveFilterDisabled = totalPillsCount === 0;

  const selectOptions =
    domainDivisions?.data.map((division) => ({
      isPrimary: division.attributes.primary,
      label: division.attributes.name,
      value: division.id,
    })) || [];

  return (
    <>
      <div className='tw-mb-4 tw-flex tw-items-center tw-justify-between'>
        <label className='tw-m-0 tw-font-medium'>
          {i18nTShared('divisionsParentheses')}
        </label>
        <button
          className={classNames(
            'tw-flex tw-h-5 tw-items-center tw-rounded-lg tw-border-0 tw-bg-white tw-px-2 tw-text-s tw-font-medium tw-tracking-wide',
            isRemoveFilterDisabled ?
              'tw-cursor-not-allowed tw-text-grey-300'
            : 'tw-cursor-pointer tw-text-grey-500 hover:tw-bg-red-025 hover:tw-text-red-500 active:tw-bg-red-050',
          )}
          disabled={isRemoveFilterDisabled}
          onClick={handleRemoveFilterClick}
          type='button'
        >
          {i18nTShared('removeFilter')}
        </button>
      </div>
      <Select
        blurInputOnSelect={false}
        canLoadMore={hasNextDomainDivisionsPage}
        className='collection-select__select-container collection-select--transparent-option'
        classNamePrefix='collection-select'
        closeMenuOnSelect={false}
        components={{
          ClearIndicator: () => null,
          DropdownIndicator,
          MenuList,
          Option,
        }}
        controlShouldRenderValue={false}
        filterOption={() => true}
        hideSelectedOptions={false}
        id='divisions'
        inputValue={searchValue}
        isLoading={isDomainDivisionsFetching}
        isMulti={true}
        isSearchable={true}
        isSelectAllChecked={isSelectAllChecked}
        isSelectAllDisabled={isDomainDivisionsFetching}
        loadingMessage={() => null}
        maxMenuHeight={244}
        menuIsOpen={isSelectMenuOpen}
        name='divisions'
        onChange={handleSelectChange}
        onInputChange={(value, { action }) => {
          if (action === 'input-change') setSearchValue(value);
        }}
        onLoadMore={handleLoadMore}
        onMenuClose={() => setIsSelectMenuOpen(false)}
        onMenuOpen={() => setIsSelectMenuOpen(true)}
        onSelectAllToggle={handleSelectAllToggle}
        openMenuOnClick={true}
        openMenuOnFocus={true}
        options={selectOptions}
        placeholder={i18nT('select.placeholder')}
        styles={{
          menuList: (provided) => ({
            ...provided,
            overscrollBehavior: 'contain',
            scrollbarGutter: 'stable',
            scrollBehavior: 'smooth',
            WebkitOverflowScrolling: 'touch',
          }),
          valueContainer: (provided) => ({
            ...provided,
            paddingLeft: `${spacing[3]} !important`,
            paddingRight: `${spacing[2]} !important`,
          }),
        }}
        value={selectOptions.filter((option) =>
          divisionIdsFilter.has(option.value),
        )}
      />
      {isSelectAllChecked && (
        <div className='tw-mt-4 tw-flex tw-gap-3 tw-rounded-lg tw-bg-amber-025 tw-p-4 tw-text-amber-800'>
          <InfoIcon className='tw-mt-0.5 tw-h-5 tw-w-5 tw-flex-shrink-0 [&_path]:tw-fill-amber-800' />
          <p className='tw-text-base tw-m-0 tw-font-normal'>
            {i18nT('noFilterApplied')}
          </p>
        </div>
      )}
      {totalPillsCount > 0 && (
        <PillsSection
          divisionIdsFilter={divisionIdsFilter}
          isSelectAllChecked={isSelectAllChecked}
          key={
            divisionIdsFilter.size === 0 && !isSelectAllChecked ?
              'no-filter'
            : 'has-filter'
          }
          onRemoveClick={handleRemovePillClick}
          totalCount={totalPillsCount}
        />
      )}
    </>
  );
}

ProjectsAnalyticsDivisionsFilter.propTypes = {
  divisionIdsFilter: PropTypes.instanceOf(Set).isRequired,
  onChange: PropTypes.func.isRequired,
};

function DropdownIndicator(props) {
  const Icon = props.selectProps.menuIsOpen ? ChevronUpIcon : ChevronDownIcon;

  return (
    <components.DropdownIndicator {...props}>
      <Icon className='[&_polygon]:tw-fill-grey-700' height={20} width={20} />
    </components.DropdownIndicator>
  );
}

function MenuList(props) {
  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  const {
    children,
    selectProps: {
      canLoadMore,
      inputValue,
      isSelectAllChecked,
      isSelectAllDisabled,
      onLoadMore,
      onSelectAllToggle,
      options,
      value: selectedOptions,
    },
  } = props;

  return (
    <components.MenuList {...props}>
      {options.length > 0 && inputValue === '' && (
        <button
          className='tw-group/toggle-all tw-flex tw-min-h-[37px] tw-w-full tw-cursor-pointer tw-items-center tw-gap-3 tw-border-0 tw-border-b-1 tw-border-solid tw-border-grey-100 tw-bg-white tw-px-3 tw-py-2.5 tw-text-left hover:tw-bg-grey-025 disabled:tw-cursor-not-allowed disabled:hover:tw-bg-white'
          disabled={isSelectAllDisabled}
          onClick={onSelectAllToggle}
          type='button'
        >
          <div
            className={classNames(
              'tw-flex tw-h-4 tw-w-4 tw-items-center tw-justify-center tw-rounded-sm tw-border-2 tw-border-solid tw-p-0',
              isSelectAllChecked || selectedOptions.length ?
                'tw-border-blue-400 tw-bg-blue-400'
              : 'tw-border-grey-300 tw-bg-white group-hover/toggle-all:tw-border-blue-300',
            )}
          >
            {isSelectAllChecked ?
              <CheckIcon className='tw-h-2 tw-min-w-[10px]' />
            : selectedOptions.length > 0 && (
                <MinusIcon className='tw-h-2 tw-min-w-[10px]' />
              )
            }
          </div>
          <span className='tw-text-m tw-font-semibold tw-text-grey-700'>
            {isSelectAllChecked || selectedOptions.length > 0 ?
              i18nTShared('deselectAll')
            : i18nTShared('selectAll')}
          </span>
        </button>
      )}
      {children}
      {options.length > 0 && canLoadMore && (
        <InView as='div' className='tw-h-1 tw-w-full' onChange={onLoadMore} />
      )}
    </components.MenuList>
  );
}

function Option(props) {
  const {
    children,
    data: { isPrimary: isPrimaryDivision },
    innerProps,
    innerRef,
    isSelected,
    selectProps: { isSelectAllChecked },
  } = props;

  const isOptionSelected = isSelectAllChecked || isSelected;

  return (
    <div
      className={classNames(
        'collection-select__option tw-flex tw-min-h-[37px] tw-cursor-pointer tw-items-center tw-gap-3 tw-px-3 tw-py-2.5',
      )}
      ref={innerRef}
      {...innerProps}
    >
      <div
        className={classNames(
          'tw-flex tw-h-4 tw-w-4 tw-items-center tw-justify-center tw-rounded-sm tw-border-2 tw-border-solid tw-p-0',
          isOptionSelected ?
            'tw-border-blue-400 tw-bg-blue-400'
          : 'tw-border-grey-300 tw-bg-white hover:tw-border-blue-300',
        )}
      >
        {isOptionSelected && <CheckIcon className='tw-h-2 tw-min-w-[10px]' />}
      </div>
      <span className='tw-flex tw-min-h-5 tw-flex-1 tw-items-center tw-gap-1.5 tw-text-m tw-font-normal tw-tracking-auto tw-text-grey-700'>
        {isPrimaryDivision && (
          <HomeIcon className='tw-h-4 tw-w-4 [&_path]:tw-fill-grey-700' />
        )}
        {children}
      </span>
    </div>
  );
}

function PillsSection({
  divisionIdsFilter,
  isSelectAllChecked,
  onRemoveClick,
  totalCount,
}) {
  const queryClient = useQueryClient();
  const pillElementsRef = useRef([]);
  const pillsContainerRef = useRef(null);

  const [isExpanded, setIsExpanded] = useState(false);
  const [hiddenCount, setHiddenCount] = useState(0);

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  const handleHiddenCountChange = (count) => {
    setHiddenCount(count);
    if (count === 0) setIsExpanded(false);
  };

  useLayoutEffect(() => {
    const container = pillsContainerRef.current;

    if (
      totalCount <= MIN_VISIBLE_PILLS_COUNT ||
      !container ||
      container.scrollHeight <= MAX_PILLS_CONTAINER_HEIGHT
    ) {
      handleHiddenCountChange(0);
      return;
    }

    let visibleCount = 0;
    const containerTop = container.getBoundingClientRect().top;

    pillElementsRef.current.some((element) => {
      if (!element) return false;

      const elementBottom =
        element.getBoundingClientRect().bottom - containerTop;

      if (elementBottom <= MAX_PILLS_CONTAINER_HEIGHT) {
        visibleCount++;
        return false;
      } else {
        return true;
      }
    });

    handleHiddenCountChange(totalCount - visibleCount);
  }, [totalCount]);

  const collectedDomainCompanies = collectDomainCompanies({ queryClient });

  const divisions = sortCompaniesByPrimaryAndName({
    companies:
      isSelectAllChecked ?
        Object.values(collectedDomainCompanies)
      : [...divisionIdsFilter].map(
          (divisionId) => collectedDomainCompanies[divisionId],
        ),
  });

  return (
    <div className='tw-mt-4'>
      <div className='tw-text-base tw-mb-3 tw-font-semibold tw-text-grey-800'>
        {i18nTShared('selectedWithCount', { count: totalCount })}
      </div>
      <div
        className={classNames(
          'tw-flex tw-flex-wrap tw-gap-2',
          !isExpanded && 'tw-overflow-hidden tw-border-0',
        )}
        ref={pillsContainerRef}
        style={
          !isExpanded && hiddenCount > 0 ?
            { maxHeight: `${MAX_PILLS_CONTAINER_HEIGHT}px` }
          : {}
        }
      >
        {divisions.map((division, index) => (
          <div
            className='tw-text-sm tw-flex tw-max-w-full tw-items-center tw-gap-2 tw-rounded-lg tw-bg-grey-050 tw-px-3 tw-py-2 tw-text-grey-700'
            key={division.id}
            ref={(element) => {
              pillElementsRef.current[index] = element || null;
            }}
          >
            {division.attributes.primary && (
              <HomeIcon className='tw-h-4 tw-w-4 tw-flex-shrink-0 [&_path]:tw-fill-grey-700' />
            )}
            <span className='tw-min-w-0 tw-flex-1 tw-truncate'>
              {division.attributes.name}
            </span>
            <button
              className='tw-h-5 tw-w-5 tw-rounded-lg tw-border-0 tw-bg-grey-050 tw-p-0 hover:tw-bg-grey-200 [&_polygon]:tw-fill-grey-500'
              onClick={() => onRemoveClick(division.id)}
            >
              <CloseIcon height={20} width={20} />
              <span className='tw-sr-only'>
                {i18nTShared('removeName', {
                  name: division.attributes.name,
                })}
              </span>
            </button>
          </div>
        ))}
      </div>
      {hiddenCount > 0 && (
        <button
          className='tw-mt-3 tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-text-m tw-font-medium tw-text-blue-500'
          onClick={() => setIsExpanded((prevIsExpanded) => !prevIsExpanded)}
          type='button'
        >
          {isExpanded ?
            i18nTShared('showLess')
          : i18nTShared('showMoreWithCount', { count: hiddenCount })}
        </button>
      )}
    </div>
  );
}

PillsSection.propTypes = {
  divisionIdsFilter: PropTypes.instanceOf(Set).isRequired,
  isSelectAllChecked: PropTypes.bool.isRequired,
  onRemoveClick: PropTypes.func.isRequired,
  totalCount: PropTypes.number.isRequired,
};
